import { z } from 'zod';
import {
  compareQty,
  isQtyString,
  positiveQtyString,
  qty,
  qtyEquals,
  qtyToString,
  nonNegativeQtyString,
} from '../quantity/index.js';
import { type Uom, type UomConversion, convert, uomCode } from '../uom/index.js';
import { stateKeySchema } from './state-rules.js';

/**
 * Recipes (vault Core Model, accepted change 10; plan §5.2). A recipe belongs to the
 * product it makes and has versions; a version is `draft` until the baker signs it off,
 * then `active`, then `retired` when a newer one takes over. At most one version of a
 * recipe is active at a time. Quantities keep full precision (decimal strings).
 * @module
 */

/** Recipe version statuses. */
export const RECIPE_VERSION_STATUSES = ['draft', 'active', 'retired'] as const;
/** One of {@link RECIPE_VERSION_STATUSES}. */
export type RecipeVersionStatus = (typeof RECIPE_VERSION_STATUSES)[number];
/** zod schema for a recipe version status. */
export const recipeVersionStatusSchema = z.enum(RECIPE_VERSION_STATUSES);

/** Allowed status changes. A retired version never comes back; a new version is made. */
export const RECIPE_VERSION_TRANSITIONS: Readonly<
  Record<RecipeVersionStatus, readonly RecipeVersionStatus[]>
> = {
  draft: ['active', 'retired'],
  active: ['retired'],
  retired: [],
};

/** True when a version may move from `from` to `to`. */
export function canTransitionRecipeVersion(
  from: RecipeVersionStatus,
  to: RecipeVersionStatus,
): boolean {
  return RECIPE_VERSION_TRANSITIONS[from].includes(to);
}

/** A loss percentage: 0 ≤ x < 100, as a decimal string. */
export const lossPctSchema = nonNegativeQtyString.refine(
  (s) => isQtyString(s) && compareQty(s, 100) < 0,
  'Loss must be below 100%',
);

/** One component line of a recipe version. */
export const recipeLineSchema = z.object({
  componentProductId: z.uuid(),
  /** Full precision, per batch, in `uom`. */
  qty: positiveQtyString,
  uom: uomCode,
  /** Expected loss (trim, evaporation), percent of `qty`. */
  lossPct: lossPctSchema,
  /**
   * State keys a component lot may be in to be used. Empty = any state that is usable as
   * an ingredient.
   */
  allowedStates: z
    .array(stateKeySchema)
    .refine((keys) => new Set(keys).size === keys.length, 'States must not repeat'),
});
/** A recipe line. */
export type RecipeLine = z.infer<typeof recipeLineSchema>;

/** A recipe version as the domain sees it. */
export const recipeVersionSchema = z.object({
  versionNo: z.int().min(1),
  status: recipeVersionStatusSchema,
  batchYieldQty: positiveQtyString,
  yieldUom: uomCode,
  instructions: z.string().nullable(),
  allergenStatement: z.string().nullable(),
  lines: z.array(recipeLineSchema),
});
/** A recipe version. */
export type RecipeVersion = z.infer<typeof recipeVersionSchema>;

/** Units in which a whole number usually means someone rounded (0.25 kg became 0 or 1). */
export const FRACTIONAL_UNITS: readonly Uom[] = ['kg', 'l'];

/** A reason to ask the baker to confirm a quantity. */
export type PrecisionWarning =
  | { code: 'whole_number_in_fractional_unit'; qty: string; uom: Uom }
  | {
      code: 'conversion_rounding';
      qty: string;
      uom: Uom;
      baseUom: Uom;
      /** The quantity in the base unit (rounded). */
      baseQty: string;
      /** Converting back gives this, not `qty`. */
      roundTrip: string;
    }
  | { code: 'missing_conversion'; uom: Uom; baseUom: Uom };

/** Options for {@link precisionWarnings}. */
export interface PrecisionOptions {
  /** The component product's base unit; enables the conversion check. */
  baseUom?: Uom;
  /** Account and product conversions (see `@enp/domain/uom`). */
  conversions?: readonly UomConversion[];
}

/**
 * Quantities worth a second look before a recipe is signed off (vault change 10: "warn
 * when rounding changes a quantity"):
 * - `whole_number_in_fractional_unit`: a whole number in kg or l (1 kg of butter may have
 *   been 0.75 kg before a system rounded it);
 * - `conversion_rounding`: converting the quantity to the component's base unit and back
 *   does not give the same value, so stock deductions would drift;
 * - `missing_conversion`: no route from the line's unit to the base unit.
 * Warnings never block; they are shown to the baker.
 */
export function precisionWarnings(
  line: Pick<RecipeLine, 'qty' | 'uom' | 'componentProductId'>,
  options: PrecisionOptions = {},
): PrecisionWarning[] {
  if (!isQtyString(line.qty)) throw new RangeError(`Not a quantity: ${JSON.stringify(line.qty)}`);
  const warnings: PrecisionWarning[] = [];
  const amount = qty(line.qty);
  if (FRACTIONAL_UNITS.includes(line.uom) && amount.isInteger()) {
    warnings.push({
      code: 'whole_number_in_fractional_unit',
      qty: qtyToString(amount),
      uom: line.uom,
    });
  }
  const { baseUom } = options;
  if (baseUom !== undefined && baseUom !== line.uom) {
    const convOptions = { productId: line.componentProductId };
    const forward = convert(amount, line.uom, baseUom, options.conversions, convOptions);
    if (!forward.ok) {
      warnings.push({ code: 'missing_conversion', uom: line.uom, baseUom });
    } else {
      const back = convert(forward.qty, baseUom, line.uom, options.conversions, convOptions);
      // A route one way always exists the other way (conversions are used both ways).
      const roundTrip = back.ok ? back.qty : forward.qty;
      if (!qtyEquals(roundTrip, amount)) {
        warnings.push({
          code: 'conversion_rounding',
          qty: qtyToString(amount),
          uom: line.uom,
          baseUom,
          baseQty: qtyToString(forward.qty),
          roundTrip: qtyToString(roundTrip),
        });
      }
    }
  }
  return warnings;
}

/** The minimum a version needs for the one-active-version invariant. */
export interface VersionStatusRef {
  recipeId: string;
  status: RecipeVersionStatus;
}

/**
 * Recipe ids that have more than one `active` version (the invariant: at most one; the
 * database enforces it too). Empty when the invariant holds.
 */
export function recipesWithSeveralActiveVersions(versions: readonly VersionStatusRef[]): string[] {
  const counts = new Map<string, number>();
  for (const v of versions) {
    if (v.status === 'active') counts.set(v.recipeId, (counts.get(v.recipeId) ?? 0) + 1);
  }
  return [...counts].filter(([, n]) => n > 1).map(([id]) => id);
}

/**
 * The active version among one recipe's versions, or `null`. Throws `RangeError` when
 * there is more than one (a broken invariant, never a choice to make silently).
 */
export function activeVersionOf<T extends { status: RecipeVersionStatus }>(
  versions: readonly T[],
): T | null {
  const active = versions.filter((v) => v.status === 'active');
  if (active.length > 1) {
    throw new RangeError(`A recipe has ${active.length} active versions; at most one is allowed`);
  }
  return active[0] ?? null;
}
